import { logger } from "./logger";
/**
 * General API Cache Utility
 * Prevents duplicate API calls and rate limiting issues for all API requests
 */
// import { logger } from "./logger";
interface CacheEntry<T> {
    data: T;
    timestamp: number;
    expiresAt: number;
}
class ApiCache {
    private cache = new Map<string, CacheEntry<any>>();
    private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
    private pendingRequests = new Map<string, Promise<any>>();
    private lastRequestTime = new Map<string, number>();
    private readonly MIN_REQUEST_INTERVAL = 5000; // 5 seconds minimum between requests
    /**
     * Get cached data if it exists and hasn't expired
     */
    get<T>(key: string): T | null {
        const entry = this.cache.get(key);
        if (!entry)
            return null;
        if (Date.now() > entry.expiresAt) {
            this.cache.delete(key);
            return null;
        }
        return entry.data;
    }
    /**
     * Set cached data with TTL
     */
    set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
        const now = Date.now();
        this.cache.set(key, {
            data,
            timestamp: now,
            expiresAt: now + ttl
        });
    }
    /**
     * Check if a request is already pending
     */
    hasPendingRequest(key: string): boolean {
        return this.pendingRequests.has(key);
    }
    /**
     * Set a pending request promise
     */
    setPendingRequest<T>(key: string, promise: Promise<T>): void {
        this.pendingRequests.set(key, promise);
    }
    /**
     * Get a pending request promise
     */
    getPendingRequest<T>(key: string): Promise<T> | null {
        return this.pendingRequests.get(key) || null;
    }
    /**
     * Clear a pending request
     */
    clearPendingRequest(key: string): void {
        this.pendingRequests.delete(key);
    }
    /**
     * Get last request time for rate limiting
     */
    getLastRequestTime(key: string): number | undefined {
        return this.lastRequestTime.get(key);
    }
    /**
     * Set last request time for rate limiting
     */
    setLastRequestTime(key: string, time: number): void {
        this.lastRequestTime.set(key, time);
    }
    /**
     * Get minimum request interval
     */
    getMinRequestInterval(): number {
        return this.MIN_REQUEST_INTERVAL;
    }
    /**
     * Clear all cache entries
     */
    clear(): void {
        this.cache.clear();
        this.pendingRequests.clear();
        this.lastRequestTime.clear();
    }
    /**
     * Get cache statistics
     */
    getStats(): {
        size: number;
        pendingRequests: number;
    } {
        return {
            size: this.cache.size,
            pendingRequests: this.pendingRequests.size
        };
    }
}
// Export singleton instance
export const apiCache = new ApiCache();
/**
 * Cached API request function with rate limiting
 */
export async function cachedApiRequest<T>(url: string, options: RequestInit = {}, ttl: number = 5 * 60 * 1000, // 5 minutes default
customCacheKey?: string): Promise<T> {
    const cacheKey = customCacheKey || `api:${url}:${JSON.stringify(options)}`;
    // Check cache first
    const cached = apiCache.get<T>(cacheKey);
    if (cached) {
        logger.api('📦 API cache hit for:', url);
        return cached;
    }
    // Check if request is already pending
    if (apiCache.hasPendingRequest(cacheKey)) {
        logger.api('⏳ API request already pending for:', url);
        return apiCache.getPendingRequest<T>(cacheKey)!;
    }
    // Rate limiting check
    const lastRequest = apiCache.getLastRequestTime(cacheKey);
    const now = Date.now();
    if (lastRequest && (now - lastRequest) < apiCache.getMinRequestInterval()) {
        logger.api('🚫 API rate limit: too soon since last request for:', url);
        // Return cached data if available, otherwise wait
        const cachedData = apiCache.get<T>(cacheKey);
        if (cachedData) {
            logger.debug('✅ Using cached data during rate limit:', { "arg0": url });
            return cachedData;
        }
        // Wait a bit and try again
        const waitTime = apiCache.getMinRequestInterval() - (now - lastRequest);
        logger.debug(`⏳ Waiting ${waitTime}ms before retry for:`, { "arg0": url });
        await new Promise(resolve => setTimeout(resolve, waitTime));
        // After waiting, check cache again in case another request populated it
        const retryCached = apiCache.get<T>(cacheKey);
        if (retryCached) {
            logger.debug('✅ Cache hit after rate limit wait:', { "arg0": url });
            return retryCached;
        }
    }
    // Make the request
    logger.api('🌐 API cache miss, making request to:', url);
    // Track request time for rate limiting
    apiCache.setLastRequestTime(cacheKey, now);
    const requestPromise = fetch(url, options)
        .then(async (response) => {
        if (!response.ok) {
            // Handle rate limiting specifically
            if (response.status === 429) {
                // Try to get retry-after header
                const retryAfter = response.headers.get('retry-after');
                const retrySeconds = retryAfter ? parseInt(retryAfter) : 30; // Default to 30 seconds
                logger.warn(`🚫 Rate limited (429), retry after ${retrySeconds} seconds:`, { "arg0": url });
                throw new Error(`Rate limited: retry after ${retrySeconds} seconds`);
            }
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
    })
        .then((data) => {
        // Cache successful response
        apiCache.set(cacheKey, data, ttl);
        return data;
    })
        .catch((error) => {
        // Don't cache errors, but handle rate limiting gracefully
        if (error.message.includes('Rate limited')) {
            logger.warn('🚫 Rate limited, will retry later:', { "arg0": url });
            // Return empty result for rate limited requests
            return { success: true, data: [], error: 'Rate limited' };
        }
        throw error;
    })
        .finally(() => {
        // Clear pending request
        apiCache.clearPendingRequest(cacheKey);
    });
    // Store pending request
    apiCache.setPendingRequest(cacheKey, requestPromise);
    return requestPromise;
}
